'use client'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@/styles/global.css'

import type { Device } from '@cdc-internal/fe-toolbox/utils/device'
import { isDevelopmentBuild } from '@cdc-internal/fe-toolbox/utils/env'
import { ColorSchemeScript, mantineHtmlProps } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { GoogleTagManager } from '@next/third-parties/google'
import { useEffect } from 'react'

import {
  GenericErrorBoundaryFallback,
  type GenericErrorBoundaryFallbackProps,
} from '@/components/error/fallback'
import AppInit from '@/components/global/AppInit'
import AppProviders from '@/components/global/AppProvider'
import PriceAppLayout from '@/components/global/layout/PriceAppLayout'
import { defaultColorScheme, logger } from '@/configs'
import { DEFAULT_CURRENCY } from '@/configs/currencies'
import { currentAppEnvConfigs } from '@/configs/env'
import type { RequestUserAgent } from '@/types/global'

const simpleMessages = {
  somethingWentWrong: 'Something went wrong',
  pleaseTryAgainOrRefreshThePage: 'Please try again or refresh the page',
  tryAgain: 'Try again',
}

const defaultRequestUserAgent: RequestUserAgent = {
  isBot: false,
  ua: '',
  browser: { name: '', version: '' },
  engine: { name: '', version: '' },
  os: { name: '', version: '' },
  cpu: { architecture: '' },
  device: {
    kind: 'desktop' as Device,
    vendor: '',
    model: '',
    type: '',
  },
}

/**
 * Add the following code to RootLayout.tsx and run scripts/local-docker.sh to test the global error page
 * Refer to https://github.com/vercel/next.js/issues/46964#issuecomment-2296918968
  if (process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD) {
    // This will allow the build, but fail at runtime
    throw new Error('Oh no!')
  }
 */

export default function GlobalError(props: GenericErrorBoundaryFallbackProps) {
  useEffect(() => {
    logger.error('Global error occurred:', props.error)
  }, [props.error])

  return (
    <html lang="en" {...mantineHtmlProps}>
      <head>
        {!isDevelopmentBuild && <GoogleTagManager gtmId={currentAppEnvConfigs.gtmId} />}

        <ColorSchemeScript defaultColorScheme={defaultColorScheme} />
      </head>
      <body id="cdc-market-body">
        <AppProviders
          messages={simpleMessages}
          requestUserAgent={defaultRequestUserAgent}
          locale="en"
          selectedCurrency={DEFAULT_CURRENCY.code}
          countryCode={'US'}
        >
          <Notifications position="top-right" containerWidth={343} />
          <AppInit />
          <PriceAppLayout locale={'en'} countryCode={'US'}>
            <GenericErrorBoundaryFallback {...props} />
          </PriceAppLayout>
        </AppProviders>
      </body>
    </html>
  )
}
